import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { brandInfo, footerExploreLinks, socialLinks } from '@/mocks/site'

const serviceKeys = [
  { key: 'weddings', to: '/services' },
  { key: 'prewedding', to: '/services' },
  { key: 'baby', to: '/services' },
  { key: 'videography', to: '/services' },
  { key: 'frames', to: '/printing' },
  { key: 'cards', to: '/printing' },
]

export default function Footer() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()

  return (
    <footer className="bg-primary-950 text-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-12 rounded-full overflow-hidden ring-1 ring-primary-400/60 shrink-0">
                <img
                  src={brandInfo.logoImage}
                  alt="Shabi Media logo"
                  title="Shabi Media — photography, videography and creative design since 1988"
                  className="w-full h-full object-cover"
                />
              </span>
              <span className="leading-none">
                <span className="block font-heading text-2xl font-semibold text-background-50">
                  {brandInfo.name}
                </span>
                <span className="block text-[11px] tracking-[0.3em] uppercase text-primary-300 mt-1">
                  {t('hero.badgeSince')}
                </span>
              </span>
            </div>
            <p className="text-sm text-background-200 leading-relaxed max-w-xs">{t('footer.blurb')}</p>
            <div className="flex items-center gap-3 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.id}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-background-50/20 text-background-200 hover:border-primary-400 hover:text-primary-300 transition-colors cursor-pointer"
                >
                  <i className={`${social.icon} text-lg`} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-lg text-background-50 mb-5">
              <Link to="/" className="cursor-pointer">{t('footer.explore')}</Link>
            </h4>
            <ul className="space-y-3">
              {footerExploreLinks.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.to}
                    className="text-sm text-background-200 hover:text-primary-300 transition-colors cursor-pointer"
                  >
                    {t(`nav.${link.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg text-background-50 mb-5">
              <Link to="/services" className="cursor-pointer">{t('footer.ourServices')}</Link>
            </h4>
            <ul className="space-y-3">
              {serviceKeys.map((item) => (
                <li key={item.key}>
                  <Link
                    to={item.to}
                    className="text-sm text-background-200 hover:text-primary-300 transition-colors cursor-pointer"
                  >
                    {t(`footer.${item.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg text-background-50 mb-5">
              <Link to="/contact" className="cursor-pointer">{t('footer.reachUs')}</Link>
            </h4>
            <ul className="space-y-4 text-sm text-background-200">
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-2-line text-primary-300 text-lg mt-0.5" />
                <span>
                  {t('footer.address')}
                  <br />
                  <span className="text-background-300/80 text-xs">{t('footer.origin')}</span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-phone-line text-primary-300 text-lg mt-0.5" />
                <a href={brandInfo.phoneLink} className="hover:text-primary-300 transition-colors cursor-pointer">
                  {brandInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-mail-line text-primary-300 text-lg mt-0.5" />
                <a href={`mailto:${brandInfo.email}`} className="hover:text-primary-300 transition-colors cursor-pointer">
                  {brandInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-time-line text-primary-300 text-lg mt-0.5" />
                <span>{t('footer.hours')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-50/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-300 text-center sm:text-left">
            © {year} {brandInfo.name}. {t('footer.rights')}
          </p>
          <p className="text-xs text-background-300 tracking-wide">{t('footer.bottom')}</p>
        </div>
      </div>
    </footer>
  )
}